import { useEffect, useState } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * A pre-rendered end card option that can be selected when creating a job.
 * The rendering pipeline substitutes the selected option's video in place of
 * the editable end frame, removing the need for per-job text/color overrides.
 */
export interface EndCardOption {
  /** Unique identifier for this end card variant. Matches the endCardId used in job payloads. */
  id: string;
  /** Human-readable label shown in the UI, e.g. "Veo on Shorts — EN". */
  label: string;
  /** BCP 47 language tag this card is designed for, e.g. "en", "ja", "de". */
  language: string;
  /**
   * Low-res MP4 proxy URL used for frontend preview only.
   * Points to: endcards/<language>/proxy/...
   */
  previewUrl?: string;
  /**
   * Original MOV URL used as Remotion render input.
   * Points to: endcards/<language>/original/...
   */
  videoUrl: string;
  /** Optional preview thumbnail URL shown in the selection UI. */
  thumbnailUrl?: string;
}

export interface EffectGroupOption {
  id: string;
  label: string;
  value: string;
  previewUrl?: string;
  assetUrl?: string;
  /** Canonical market code for this option (e.g. "kr", "id", "global"). */
  market?: string;
  /** Additional market aliases that should match this option (e.g. ["en", "global"]). */
  marketAliases?: string[];
}

export interface EffectGroup {
  id: string;
  label: string;
  description?: string;
  options: EffectGroupOption[];
}

export interface Template {
  id: string;
  name: string;
  /** Semver string, e.g. "1.0". Increment on any slot structure change. */
  version: string;
  /** Alias for version — aligned with the spec field name template_version. */
  template_version: string;
  duration: number;
  slots: TemplateSlot[];
  status: 'active' | 'inactive';
  /**
   * Deliverable format identifier (e.g. "V1", "S1", "veo_shorts_v1").
   * Matches deliverable_id in the Creative Generator manifest.
   * Defaults to "veo_shorts_v1" for backward compatibility with legacy documents.
   */
  deliverable_id: string;
  /**
   * High-level format category.
   * Defaults to "shorts_featured_video" for backward compatibility.
   */
  format: 'shorts_featured_video' | 'full_screen_interstitial';
  /**
   * Output container format for the Remotion render.
   * Defaults to "mp4" for backward compatibility.
   */
  output_format: 'mp4' | 'png';
  /** Default BCP 47 language tag for this template, e.g. "en". */
  language?: string;
  /**
   * Pre-rendered end card options available for this template.
   * Job callers select one by endCardId; the rendering pipeline substitutes
   * the chosen video in place of the editable end frame.
   * When absent the template falls back to the legacy editable end-frame slots.
   */
  endCardOptions?: EndCardOption[];
  effectGroups?: EffectGroup[];
  remotionServeUrl?: string;
  remotionCompositionId?: string;
  previewImageUrl?: string;
  previewVideoUrl?: string;
  /**
   * Firebase Storage URL for the loading screen video asset.
   * When set, the Remotion composition uses this URL instead of a local static file.
   * Points to: assets/videos/loading_h264_24fps_v1.mov in Storage.
   */
  loadingVideoUrl?: string;
  /** Optional display-title metadata used by job list naming. */
  displayTitle?: { slotId?: string };
  /** Backward-compatible short form of displayTitle.slotId. */
  displayTitleField?: string;
  createdAt: Date;
}

/** Specs describing how an asset should be fitted inside the Remotion slot. */
export interface SlotSpecs {
  asset_type?: 'image' | 'video' | null;
  aspect_ratio?: string | null;
  max_duration_seconds?: number | null;
  /** Fitting mode applied inside Remotion at render time. */
  fit_mode?: 'cover' | 'contain' | null;
  /** Default crop anchor when fit_mode is "cover". Operator can override. */
  crop_anchor?: string | null;
  /** Whether to show the crop anchor UI control. Defaults to true. */
  show_crop_anchor?: boolean | null;
  language?: string | null;
  max_chars?: number | null;
  /** Metadata-driven file extension allowlist for video/image slots (e.g., [".webm"]) */
  accepted_extensions?: string[] | null;
  /** Metadata-driven MIME type allowlist for video/image slots (e.g., ["video/webm"]) */
  accepted_mime_types?: string[] | null;
  /** Whether the asset must contain an alpha channel (for transparent WebM, etc.) */
  alpha_required?: boolean | null;
  /** Default value for text or color slots */
  default?: string | null;
}

/** Source mapping for automatic slot filling from a Creative Generator run. */
export interface SlotMapFrom {
  deliverable_id?: string;
  scene_id?: string;
  element_id?: string;
  source?: 'jobs' | 'text_items';
}

/** Optional metadata-driven trim configuration for video slots. */
export interface SlotTrimConfig {
  enabled?: boolean;
  mode?: 'fixed-duration' | string;
  duration_frames?: number | null;
  fps?: number | null;
  loop_preview?: boolean | null;
}

/**
 * Optional per-slot reference preview metadata.
 * When present the right-sidebar "Slot Preview" panel shows a looping clip from
 * the template's previewVideoUrl so the operator can see exactly where in the
 * final rendered video this slot appears.
 */
export interface SlotReferencePreview {
  /** Must be "templatePreview" — indicates the source is the template's previewVideoUrl. */
  source: 'templatePreview';
  /** Time (seconds) in the preview video to seek to for the reference loop start. */
  startTimeSeconds: number;
  /** Duration (seconds) of the looped reference clip. */
  durationSeconds: number;
  /** Optional human-readable label, e.g. "Opening sequence (0s–6s)". */
  label?: string;
}

export interface TemplateSelectOption {
  label: string;
  value: string;
}

export interface TemplateSlot {
  slotId: string;
  label: string;
  type: 'image' | 'video' | 'text' | 'audio' | 'select' | 'color';
  description?: string;
  required: boolean;
  /**
   * Scene this slot belongs to, used for UI grouping.
   * When present, takes priority over map_from.scene_id.
   */
  scene_id?: string;
  /** Whether to display this field in the UI. Defaults to true. */
  visible?: boolean;
  /** Pipeline mapping — kept for future pipeline mode, not implemented in standalone MVP. */
  map_from?: SlotMapFrom;
  specs?: SlotSpecs;
  trim?: SlotTrimConfig;
  /**
   * Optional per-slot reference preview.
   * When set the right-sidebar shows a looping clip from the template's
   * previewVideoUrl at the position this slot occupies in the final render.
   * Opening video slots (video1–video5) use a dynamically computed reference
   * instead, so this field is typically absent for them.
   */
  referencePreview?: SlotReferencePreview;
  defaultValue?: string;
  options?: TemplateSelectOption[];
}

export function useTemplates() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('[useTemplates] Starting template fetch...');
    setLoading(true);
    setError(null);

    // Set up real-time listener for active templates
    const q = query(
      collection(db, 'templates'),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc')
    );

    console.log('[useTemplates] Setting up Firestore listener...');

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        console.log(`[useTemplates] Received ${snapshot.docs.length} templates from Firestore`);
        const templatesData = snapshot.docs.map((doc) => {
          const data = doc.data();
          console.log(`[useTemplates] Template: ${doc.id}`, data);
          return {
            id: doc.id,
            name: data.name,
            version: data.version || data.template_version || '1.0',
            template_version: data.template_version || data.version || '1.0',
            duration: data.duration,
            slots: (data.slots || []).map((s: Record<string, unknown>) => ({
              ...s,
              slotId: (s.slotId ?? s.id) as string,
            })),
            status: data.status,
            // New schema fields — defaulted for backward compat with legacy documents
            deliverable_id: data.deliverable_id ?? 'veo_shorts_v1',
            format: data.format ?? 'shorts_featured_video',
            output_format: data.output_format ?? 'mp4',
            language: data.language ?? undefined,
            endCardOptions: data.endCardOptions ?? undefined,
            effectGroups: data.effectGroups ?? undefined,
            remotionServeUrl: data.remotionServeUrl,
            remotionCompositionId: data.remotionCompositionId,
            previewImageUrl: data.previewImageUrl,
            previewVideoUrl: data.previewVideoUrl,
            loadingVideoUrl: data.loadingVideoUrl ?? undefined,
            displayTitle: data.displayTitle ?? undefined,
            displayTitleField: data.displayTitleField ?? undefined,
            createdAt: data.createdAt?.toDate() || new Date(),
          };
        });
        console.log('[useTemplates] Processed templates:', templatesData);
        setTemplates(templatesData);
        setLoading(false);
      },
      (err) => {
        console.error('[useTemplates] Error fetching templates:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => {
      console.log('[useTemplates] Cleaning up listener');
      unsubscribe();
    };
  }, []);

  return {
    templates,
    loading,
    error,
  };
}
